// Recorded Polish announcements (ADR-0015, decision 2026-10-09). Not speech synthesis: the
// owner generates one MP3 per entry from `text` (a TTS service or a recording), saves it in
// ../clips under `file`, and both clients play the same voice, offline, on every phone.
// Announcements never contain names: the name is on screen and in the notification.
// Until a file exists, the client plays only its own short tone for that alert.

export type SoundClip = {
  /** snake_case, the clip id: Android takes the clips as notification sounds (res/raw names). */
  file: string;
  text: string;
  /** Where it plays: the HQ panel, the responder's phone, or both. */
  on: "panel" | "phone" | "both";
};

export const SOUND_CLIPS = {
  sos: { file: "sos.mp3", text: "Uwaga! Sygnał SOS!", on: "panel" },
  message: { file: "message.mp3", text: "Nowa wiadomość.", on: "both" },
  finding: { file: "finding.mp3", text: "Nowe znalezisko.", on: "panel" },
  recall: { file: "recall.mp3", text: "Ratownik potwierdził powrót.", on: "panel" },
  joined: { file: "joined.mp3", text: "Nowy uczestnik w akcji.", on: "panel" },
  tracking_stopped: {
    file: "tracking_stopped.mp3",
    text: "Uwaga! Telefon ratownika przestał zapisywać trasę.",
    on: "panel",
  },
  no_position: { file: "no_position.mp3", text: "Uwaga! Brak pozycji ratownika.", on: "panel" },
  connection_lost: {
    file: "connection_lost.mp3",
    text: "Uwaga! Utracono połączenie z serwerem.",
    on: "panel",
  },
  connection_back: { file: "connection_back.mp3", text: "Połączenie przywrócone.", on: "both" },
  sector_assigned: { file: "sector_assigned.mp3", text: "Przydzielono nowy sektor.", on: "phone" },
  // HQ changed something about me, my group or my sector (issue #151).
  sector_changed: {
    file: "sector_changed.mp3",
    text: "Sztab zmienił twój sektor. Sprawdź mapę.",
    on: "phone",
  },
  sector_released: {
    file: "sector_released.mp3",
    text: "Sztab zakończył przydział twojego sektora.",
    on: "phone",
  },
  group_moved: { file: "group_moved.mp3", text: "Sztab zmienił twoją grupę.", on: "phone" },
  mate_joined: {
    file: "mate_joined.mp3",
    text: "Do twojej grupy dołączyła nowa osoba.",
    on: "phone",
  },
  mate_left: {
    file: "mate_left.mp3",
    text: "Osoba z twojej grupy przeszła do innej grupy.",
    on: "phone",
  },
  group_updated: {
    file: "group_updated.mp3",
    text: "Sztab zmienił dane twojej grupy.",
    on: "phone",
  },
  me_updated: { file: "me_updated.mp3", text: "Sztab zmienił twoje dane w akcji.", on: "phone" },
  subject_updated: {
    file: "subject_updated.mp3",
    text: "Nowe informacje o osobie zaginionej. Sprawdź kartę osoby.",
    on: "phone",
  },
  hq_finding: { file: "hq_finding.mp3", text: "Sztab oznaczył znalezisko na mapie.", on: "phone" },
  recall_issued: {
    file: "recall_issued.mp3",
    text: "Wezwanie do powrotu! Potwierdź i wracaj do bazy.",
    on: "phone",
  },
  sos_sent: { file: "sos_sent.mp3", text: "Sygnał SOS wysłany.", on: "phone" },
  sos_acknowledged: {
    file: "sos_acknowledged.mp3",
    text: "Sztab przyjął Twój sygnał SOS. Pomoc jest w drodze.",
    on: "phone",
  },
  gps_lost: { file: "gps_lost.mp3", text: "Uwaga! Utracono sygnał GPS.", on: "phone" },
  network_lost: {
    file: "network_lost.mp3",
    text: "Brak zasięgu. Dane zapisują się w telefonie.",
    on: "phone",
  },
  tracking_paused: {
    file: "tracking_paused.mp3",
    text: "Uwaga! Zapis trasy zatrzymany.",
    on: "phone",
  },
  battery_low: { file: "battery_low.mp3", text: "Niski poziom baterii.", on: "phone" },
  // The automatic status (issue #212).
  auto_searching: {
    file: "auto_searching.mp3",
    text: "Jesteś w sektorze. Status: szukam.",
    on: "phone",
  },
  auto_transit: {
    file: "auto_transit.mp3",
    text: "Jesteś poza sektorem. Status: w drodze.",
    on: "phone",
  },
  auto_paused: { file: "auto_paused.mp3", text: "Postój. Status: przerwa.", on: "phone" },
} as const satisfies Record<string, SoundClip>;

export type SoundClipId = keyof typeof SOUND_CLIPS;

/**
 * The "Ratun działa" tone, every 5 minutes while the track is recorded (issue #135). A tone, not
 * an announcement: generated by scripts/heartbeat.py, kept with the clips so the app ships it.
 */
export const HEARTBEAT_FILE = "heartbeat.mp3";
